import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

// Tabs container
const TabsContainer = styled.div`
  background: ${({ theme }) =>
    theme?.colors?.card?.background ||
    (theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(255, 255, 255, 0.95)')};
  border: 1px solid
    ${({ theme }) =>
      theme?.colors?.card?.border ||
      (theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgb(226, 232, 240)')};
  border-radius: 8px;
  padding: 8px;
  display: flex;
  gap: 4px;
  overflow-x: auto;

  /* Hide scrollbar but allow scrolling */
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

// Individual tab button
const TabButton = styled.button`
  padding: ${({ size }) =>
    size === 'small'
      ? '8px 16px'
      : size === 'large'
      ? '12px 24px'
      : '10px 20px'};
  background: ${({ active }) =>
    active ? 'linear-gradient(135deg, #fa4616, #ff6b3b)' : 'transparent'};
  color: ${({ active, theme }) =>
    active ? '#ffffff' : theme?.name === 'dark' ? '#94a3b8' : '#64748b'};
  border: none;
  border-radius: 6px;
  font-family: inherit;
  font-size: ${({ size }) =>
    size === 'small' ? '13px' : size === 'large' ? '15px' : '14px'};
  font-weight: ${({ active }) => (active ? 600 : 500)};
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: ${({ active }) =>
      active
        ? 'linear-gradient(135deg, #fa4616, #ff6b3b)'
        : 'rgba(250, 70, 22, 0.1)'};
    color: ${({ active }) => (active ? '#ffffff' : '#fa4616')};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: ${({ size }) =>
      size === 'small' ? '14px' : size === 'large' ? '18px' : '16px'};
    height: ${({ size }) =>
      size === 'small' ? '14px' : size === 'large' ? '18px' : '16px'};
  }
`;

// Badge for tab counts
const TabBadge = styled.span`
  background: ${({ active }) =>
    active ? 'rgba(255, 255, 255, 0.2)' : 'rgba(250, 70, 22, 0.15)'};
  color: ${({ active }) => (active ? '#ffffff' : '#fa4616')};
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  min-width: 20px;
  text-align: center;
`;

// Error indicator badge
const ErrorBadge = styled.span`
  background: #ef4444;
  color: white;
  font-size: 10px;
  font-weight: 700;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

/**
 * Tab Component - Individual tab button
 */
const Tab = ({
  children,
  active,
  onClick,
  icon,
  count,
  size,
  disabled,
  error,
}) => {
  return (
    <TabButton
      type="button"
      active={active}
      onClick={onClick}
      size={size}
      disabled={disabled}
    >
      {icon && <span className="tab-icon">{icon}</span>}
      {children}
      {error && (
        <ErrorBadge title="This tab has validation errors">!</ErrorBadge>
      )}
      {count !== undefined && count !== null && (
        <TabBadge active={active}>{count}</TabBadge>
      )}
    </TabButton>
  );
};

Tab.propTypes = {
  children: PropTypes.node.isRequired,
  active: PropTypes.bool,
  onClick: PropTypes.func,
  icon: PropTypes.node,
  count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  disabled: PropTypes.bool,
  error: PropTypes.bool,
};

/**
 * Tabs Component - Container for tab navigation
 *
 * @example
 * // Basic usage
 * <Tabs>
 *   <Tab active>Details</Tab>
 *   <Tab>Settings</Tab>
 *   <Tab>Users</Tab>
 * </Tabs>
 *
 * @example
 * // With tabs prop
 * <Tabs
 *   tabs={[
 *     { id: 'details', label: 'Details' },
 *     { id: 'settings', label: 'Settings' },
 *   ]}
 *   activeTab="details"
 *   onTabChange={(tabId) => setActiveTab(tabId)}
 * />
 *
 * @example
 * // With icons and counts
 * <Tabs
 *   tabs={[
 *     { id: 'users', label: 'Users', count: 12 },
 *     { id: 'settings', label: 'Settings', icon: <SettingsIcon /> },
 *   ]}
 *   activeTab={activeTab}
 *   onTabChange={setActiveTab}
 * />
 */
const Tabs = ({
  children,
  tabs,
  activeTab,
  onTabChange,
  size = 'medium',
  className,
}) => {
  // If tabs array is provided, render from that
  if (tabs && tabs.length > 0) {
    return (
      <TabsContainer className={className}>
        {tabs.map((tab) => (
          <Tab
            key={tab.id}
            active={activeTab === tab.id}
            onClick={() => onTabChange?.(tab.id)}
            icon={tab.icon}
            count={tab.count}
            size={size}
            disabled={tab.disabled}
            error={tab.error}
          >
            {tab.label}
          </Tab>
        ))}
      </TabsContainer>
    );
  }

  // Otherwise render children directly
  return <TabsContainer className={className}>{children}</TabsContainer>;
};

Tabs.propTypes = {
  /** Direct children (Tab components) */
  children: PropTypes.node,
  /** Array of tab configurations */
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.node,
      count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      disabled: PropTypes.bool,
      error: PropTypes.bool,
    })
  ),
  /** Currently active tab ID (when using tabs prop) */
  activeTab: PropTypes.string,
  /** Callback when tab changes (when using tabs prop) */
  onTabChange: PropTypes.func,
  /** Size of all tabs */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  /** Additional CSS class */
  className: PropTypes.string,
};

// Export both the container and individual tab
export { Tab, TabsContainer };
export default Tabs;
